import { Pressable, StyleSheet, View } from 'react-native';
import { CheckCircle2, Circle, Clock3, GripVertical } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type StudyPlanTaskCardProps = {
  title: string;
  subject: string;
  estimatedMinutes: number;
  isCompleted?: boolean;
  statusLabel: string;
  rewardLabel: string;
  onPress?: () => void;
  onToggleComplete?: () => void;
};

export function StudyPlanTaskCard({
  title,
  subject,
  estimatedMinutes,
  isCompleted = false,
  statusLabel,
  rewardLabel,
  onPress,
  onToggleComplete,
}: StudyPlanTaskCardProps) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [pressed ? styles.pressed : null]}
    >
      <AppCard style={styles.card}>
        <View style={styles.header}>
          <Pressable
            accessibilityRole="checkbox"
            accessibilityState={{ checked: isCompleted }}
            onPress={onToggleComplete}
            style={styles.checkButton}
          >
            {isCompleted ? (
              <CheckCircle2 size={22} color={tokens.colors.success} />
            ) : (
              <Circle size={22} color={tokens.colors.textMuted} />
            )}
          </Pressable>
          <View style={styles.copy}>
            <AppText variant="subtitle" weight="bold">
              {title}
            </AppText>
            <View style={styles.metaRow}>
              <Clock3 size={14} color={tokens.colors.textMuted} />
              <AppText variant="caption" tone="muted">
                {subject} · {estimatedMinutes} 分钟
              </AppText>
            </View>
          </View>
          <View style={styles.statusColumn}>
            <GripVertical size={16} color={tokens.colors.textSubtle} />
            <View style={styles.statusPill}>
              <AppText variant="caption" tone="primary" weight="bold">
                {statusLabel}
              </AppText>
            </View>
            {rewardLabel ? (
              <AppText variant="caption" tone="success" weight="bold">
                {rewardLabel}
              </AppText>
            ) : null}
          </View>
        </View>
      </AppCard>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  pressed: {
    opacity: 0.86,
  },
  card: {
    gap: tokens.spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  checkButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  copy: {
    flex: 1,
    gap: tokens.spacing.sm,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  statusColumn: {
    alignItems: 'flex-end',
    gap: tokens.spacing.xs,
  },
  statusPill: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: tokens.colors.surfaceMuted,
  },
});
